import { useEffect, useState, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { colors, formatNumber, formatPercent } from '../../styles/tokens'
import { ErrorBanner, SkeletonRows, EmptyState } from '../../components/Feedback'
import EstimationBreadcrumb from '../../components/EstimationBreadcrumb'

// =======================================================================
// Estimating KPIs — Wave 4.2. Read-only intelligence layer. Every number
// here is either read directly from an existing calculation-chain View,
// or a simple ratio computed from two values already present in one of
// those Views (e.g. overhead / tender_price_total). No business logic
// (costing, markup, overhead, contingency) is duplicated or recomputed.
// =======================================================================

// Same strict numeric normalization used by TenderSummaryPage (P1) /
// RevisionComparisonPage (P2) / RevisionWorkspacePage (P3).
function toFinancialNumber(value, fieldPath) {
  if (value === undefined || value === null) return 0
  const n = Number(value)
  if (!Number.isFinite(n)) {
    throw new Error(`Frozen snapshot contains a non-numeric financial value at ${fieldPath}: ${JSON.stringify(value)}`)
  }
  return n
}

// Same buildFrozenChain as TenderSummaryPage/RevisionComparisonPage:
// addition/subtraction over values freeze_estimate_revision already
// wrote verbatim at freeze time. No financial formula is recomputed.
function buildFrozenChain(snap) {
  const boqData = Array.isArray(snap.full_boq_data) ? snap.full_boq_data : []
  const indirect = snap.full_indirect_data && typeof snap.full_indirect_data === 'object' ? snap.full_indirect_data : {}
  const pkgCosts = Array.isArray(indirect.package_specific_costs) ? indirect.package_specific_costs : []

  let boqSiteOverhead = 0, boqRisk = 0, boqItemMarkup = 0
  boqData.forEach((item, i) => {
    boqSiteOverhead += toFinancialNumber(item.boq_specific_site_overhead, `full_boq_data[${i}].boq_specific_site_overhead`)
    boqRisk += toFinancialNumber(item.boq_specific_risk, `full_boq_data[${i}].boq_specific_risk`)
    boqItemMarkup += toFinancialNumber(item.selling_price, `full_boq_data[${i}].selling_price`) - toFinancialNumber(item.cost_base, `full_boq_data[${i}].cost_base`)
  })

  let pkgSiteOverhead = 0, pkgRisk = 0, pkgMarkup = 0
  pkgCosts.forEach((pkg, i) => {
    pkgSiteOverhead += toFinancialNumber(pkg.package_specific_site_overhead, `package_specific_costs[${i}].package_specific_site_overhead`)
    pkgRisk += toFinancialNumber(pkg.package_specific_risk, `package_specific_costs[${i}].package_specific_risk`)
    pkgMarkup += toFinancialNumber(pkg.package_specific_cost_with_markup, `package_specific_costs[${i}].package_specific_cost_with_markup`)
      - toFinancialNumber(pkg.package_specific_cost, `package_specific_costs[${i}].package_specific_cost`)
  })

  return {
    direct_cost_total: toFinancialNumber(snap.direct_cost_total, 'direct_cost_total'),
    boq_specific_site_overhead_total: boqSiteOverhead,
    boq_specific_risk_total: boqRisk,
    package_specific_site_overhead_total: pkgSiteOverhead,
    package_specific_risk_total: pkgRisk,
    package_specific_markup_total: pkgMarkup,
    general_site_overhead_total: toFinancialNumber(indirect.general_site_overhead_total, 'full_indirect_data.general_site_overhead_total'),
    ho_overhead_total: toFinancialNumber(snap.ho_overhead_total, 'ho_overhead_total'),
    general_contingency_total: toFinancialNumber(indirect.general_contingency_total, 'full_indirect_data.general_contingency_total'),
    boq_item_markup_total: boqItemMarkup,
    markup_total: toFinancialNumber(snap.markup_total, 'markup_total'),
    tender_price_total: toFinancialNumber(snap.tender_price_total, 'tender_price_total'),
  }
}

export default function EstimatingKPIsPage() {
  const { tenderId, revisionId } = useParams()
  const [chain, setChain] = useState(null)
  const [statusCounts, setStatusCounts] = useState(null)
  const [wbsPackages, setWbsPackages] = useState(null)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    const revRes = await supabase.from('estimate_revision').select('status').eq('id', revisionId).maybeSingle()
    if (revRes.error) return setError(revRes.error.message)

    if (revRes.data?.status === 'frozen') {
      // Frozen: Cost Summary / Overhead Breakdown are fully resolvable
      // from the immutable freeze_snapshot (same fields proven exact in
      // TenderSummaryPage/RevisionComparisonPage). Pricing-status counts
      // and WBS package cost distribution are NOT resolvable -- their
      // live sources (boq_item_pricing_status depends on rate_buildup_line
      // detail; wbs_package_total_cost depends on boq_item_wbs_map) were
      // never captured in the snapshot. Rather than fabricate a
      // misleading classification or a partial cost figure, those
      // sections are shown empty/zero here -- an honest "not available",
      // not an invented number.
      const snapRes = await supabase
        .from('freeze_snapshot')
        .select('direct_cost_total, ho_overhead_total, markup_total, tender_price_total, full_boq_data, full_indirect_data')
        .eq('estimate_revision_id', revisionId)
        .maybeSingle()
      if (snapRes.error) return setError(snapRes.error.message)
      if (!snapRes.data) return setError('This revision is marked frozen but no snapshot record could be found.')

      try {
        setChain(buildFrozenChain(snapRes.data))
      } catch (e) {
        return setError(e.message)
      }
      setStatusCounts({ priced: 0, unpriced: 0, incomplete: 0, zero_priced: 0, excluded: 0 })
      setWbsPackages([])
      return
    }

    const [chainRes, statusRes, wbsRes] = await Promise.all([
      supabase.from('estimate_revision_calculation_chain').select('*').eq('estimate_revision_id', revisionId).maybeSingle(),
      supabase.from('boq_item_pricing_status').select('pricing_status').eq('estimate_revision_id', revisionId),
      supabase.from('wbs_package_total_cost').select('wbs_node_id, wbs_package_total_cost').eq('estimate_revision_id', revisionId),
    ])
    if (chainRes.error) return setError(chainRes.error.message)
    if (statusRes.error) return setError(statusRes.error.message)
    if (wbsRes.error) return setError(wbsRes.error.message)

    setChain(chainRes.data)

    const counts = { priced: 0, unpriced: 0, incomplete: 0, zero_priced: 0, excluded: 0 }
    for (const row of statusRes.data || []) {
      if (counts[row.pricing_status] !== undefined) counts[row.pricing_status]++
    }
    setStatusCounts(counts)

    // wbs_package_total_cost is a VIEW (not a table), so PostgREST cannot
    // reliably resolve an embedded wbs_snapshot_node(name) join through
    // it — fetch node names in a separate, explicit query instead.
    const positiveWbs = (wbsRes.data || []).filter((w) => Number(w.wbs_package_total_cost) > 0)
    if (positiveWbs.length > 0) {
      const { data: nodeNames, error: nodeErr } = await supabase
        .from('wbs_snapshot_node')
        .select('id, name')
        .in('id', positiveWbs.map((w) => w.wbs_node_id))
      if (nodeErr) return setError(nodeErr.message)
      const nameMap = Object.fromEntries((nodeNames || []).map((n) => [n.id, n.name]))
      setWbsPackages(positiveWbs.map((w) => ({ ...w, nodeName: nameMap[w.wbs_node_id] || '—' })))
    } else {
      setWbsPackages([])
    }
  }, [revisionId])

  useEffect(() => { load() }, [load])

  if (error) return <ErrorBanner message={error} />
  if (chain === undefined || statusCounts === null) return <SkeletonRows rows={4} cols={4} />

  if (!chain) {
    return (
      <div>
        <BackLink tenderId={tenderId} revisionId={revisionId} />
        <PageHeader />
        <div style={{ padding: 24, background: colors.warningBg, color: colors.warningText, borderRadius: 8, fontSize: 14 }}>
          No calculation chain data yet — add BOQ items and price them first.
        </div>
      </div>
    )
  }

  const totalItems = Object.values(statusCounts).reduce((a, b) => a + b, 0)
  const pricedEligible = totalItems - statusCounts.excluded // completion is measured against non-excluded items only
  const completionPct = pricedEligible > 0 ? (statusCounts.priced / pricedEligible) * 100 : null

  const overheadTotal = Number(chain.general_site_overhead_total) + Number(chain.ho_overhead_total)
  const overheadPct = chain.tender_price_total > 0 ? (overheadTotal / chain.tender_price_total) * 100 : null
  const contingencyPct = chain.tender_price_total > 0 ? (Number(chain.general_contingency_total) / chain.tender_price_total) * 100 : null
  // Markup % expressed against the cost base it was applied to (direct cost
  // + general site overhead), matching how markup is actually calculated
  // in boq_item_selling_price — not against the final tender price, which
  // would understate it since overheads/contingency are added after markup.
  const markupBase = Number(chain.direct_cost_total) + Number(chain.general_site_overhead_total)
  const markupPct = markupBase > 0 ? (Number(chain.markup_total) / markupBase) * 100 : null

  return (
    <div>
      <BackLink tenderId={tenderId} revisionId={revisionId} />
      <PageHeader />

      {/* ---------------------------------------------------------- */}
      {/* Section 1 — Pricing Completion                              */}
      {/* ---------------------------------------------------------- */}
      <SectionTitle>Pricing Completion</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
        <KpiCard
          label="Completion"
          value={completionPct != null ? `${formatNumber(completionPct)}%` : '—'}
          sublabel={`${statusCounts.priced} of ${pricedEligible} priced`}
          highlight
        />
        <KpiCard label="Priced" value={statusCounts.priced} tone="success" />
        <KpiCard label="Unpriced" value={statusCounts.unpriced} tone="danger" />
        <KpiCard label="Incomplete / Rate Missing" value={statusCounts.incomplete} tone="warning" />
        <KpiCard label="Zero Priced" value={statusCounts.zero_priced} tone="warning" />
        <KpiCard label="Excluded" value={statusCounts.excluded} tone="neutral" />
      </div>

      {/* ---------------------------------------------------------- */}
      {/* Section 2 — Cost Summary                                    */}
      {/* ---------------------------------------------------------- */}
      <SectionTitle>Cost Summary</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
        <KpiCard label="Direct Cost" value={`${formatNumber(chain.direct_cost_total)} SAR`} />
        <KpiCard label="Tender Price" value={`${formatNumber(chain.tender_price_total)} SAR`} highlight />
        <KpiCard label="Overhead %" value={overheadPct != null ? `${formatNumber(overheadPct)}%` : '—'} sublabel="Site + Head Office" />
        <KpiCard label="Contingency %" value={contingencyPct != null ? `${formatNumber(contingencyPct)}%` : '—'} />
        <KpiCard label="Markup %" value={markupPct != null ? `${formatNumber(markupPct)}%` : '—'} sublabel="On direct cost + site OH" />
      </div>

      {/* ---------------------------------------------------------- */}
      {/* Section 3 — Site OH vs HO OH Split                          */}
      {/* ---------------------------------------------------------- */}
      <SectionTitle>Overhead Breakdown</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 }}>
        <KpiCard label="General Site Overhead" value={`${formatNumber(chain.general_site_overhead_total)} SAR`} />
        <KpiCard label="Head Office Overhead" value={`${formatNumber(chain.ho_overhead_total)} SAR`} />
        <KpiCard
          label="Site : HO Ratio"
          value={
            overheadTotal > 0
              ? `${formatNumber((Number(chain.general_site_overhead_total) / overheadTotal) * 100)}% : ${formatNumber((Number(chain.ho_overhead_total) / overheadTotal) * 100)}%`
              : '—'
          }
          sublabel="Share of combined overhead"
        />
      </div>

      {/* ---------------------------------------------------------- */}
      {/* Section 4 — Cost Distribution by WBS Package                */}
      {/* ---------------------------------------------------------- */}
      <SectionTitle>Cost Distribution by WBS Package</SectionTitle>
      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden' }}>
        {wbsPackages === null && <div style={{ padding: 16 }}><SkeletonRows rows={2} cols={2} /></div>}
        {wbsPackages?.length === 0 && (
          <div style={{ padding: 24 }}>
            <EmptyState
              title="No WBS cost data available"
              description="No BOQ items are currently linked to a WBS package with priced costs. This is expected until items are mapped to WBS packages and priced."
            />
          </div>
        )}
        {wbsPackages?.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#FAFBFC', borderBottom: `1px solid ${colors.border}` }}>
                <Th>WBS Package</Th>
                <Th align="right">Total Cost (SAR)</Th>
                <Th align="right">% of Direct Cost</Th>
              </tr>
            </thead>
            <tbody>
              {wbsPackages
                .sort((a, b) => Number(b.wbs_package_total_cost) - Number(a.wbs_package_total_cost))
                .map((w) => (
                  <tr key={w.wbs_node_id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <Td>{w.nodeName}</Td>
                    <Td align="right" style={{ fontWeight: 600 }}>{formatNumber(w.wbs_package_total_cost)}</Td>
                    <Td align="right" muted>
                      {chain.direct_cost_total > 0 ? `${formatNumber((Number(w.wbs_package_total_cost) / Number(chain.direct_cost_total)) * 100)}%` : '—'}
                    </Td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>

      <p style={{ fontSize: 11, color: colors.textMuted, marginTop: 20 }}>
        All figures are read directly from <code>estimate_revision_calculation_chain</code>, <code>boq_item_pricing_status</code>,
        and <code>wbs_package_total_cost</code> — no calculation is performed in this page.
      </p>
    </div>
  )
}

function BackLink() {
  return <EstimationBreadcrumb />
}

function PageHeader() {
  return (
    <>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary, margin: '10px 0 4px' }}>Estimating KPIs</h1>
      <p style={{ fontSize: 13, color: colors.textSecondary, margin: '0 0 20px' }}>
        Read-only performance and completeness indicators for this revision.
      </p>
    </>
  )
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 700, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 10 }}>
      {children}
    </div>
  )
}

const TONE_COLORS = {
  success: '#166534',
  danger: '#991B1B',
  warning: '#9A3412',
  neutral: '#6B7280',
}

function KpiCard({ label, value, sublabel, highlight, tone }) {
  return (
    <div style={{
      padding: '14px 16px', borderRadius: 8, background: highlight ? '#F8FAFF' : colors.surface,
      border: `1px solid ${highlight ? colors.primary : colors.border}`,
    }}>
      <div style={{ fontSize: 11, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: highlight ? 24 : 20, fontWeight: 800, color: tone ? TONE_COLORS[tone] : highlight ? colors.primary : colors.textPrimary }}>
        {value}
      </div>
      {sublabel && <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 3 }}>{sublabel}</div>}
    </div>
  )
}

function Th({ children, align = 'left' }) {
  return <th style={{ textAlign: align, padding: '10px 16px', fontSize: 12, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4 }}>{children}</th>
}
function Td({ children, align = 'left', muted, style }) {
  return <td style={{ textAlign: align, padding: '11px 16px', color: muted ? colors.textMuted : colors.textPrimary, fontSize: 14, ...style }}>{children}</td>
}
